import React, { useEffect, useState, useCallback } from "react";
import { useRecordContext, useNotify } from "react-admin";
import { Box, Grid, Paper, Typography, Button, CircularProgress, Chip, Divider } from "@mui/material";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/pt-br";
import { get, post } from "../../lib/request";
import { DEFAULT_API_ADMIN } from "../../constants";

dayjs.extend(relativeTime);

// App Store Connect "appStoreState" / "appVersionState" enum → PT.
// Ref: developer.apple.com/documentation/appstoreconnectapi/appstoreversionstate
const ASC_STATE_PT: Record<string, string> = {
  ACCEPTED: "Aceito",
  DEVELOPER_REJECTED: "Rejeitado pelo desenvolvedor",
  DEVELOPER_REMOVED_FROM_SALE: "Removido da venda (desenvolvedor)",
  IN_REVIEW: "Em revisão",
  INVALID_BINARY: "Binário inválido",
  METADATA_REJECTED: "Metadados rejeitados",
  PENDING_APPLE_RELEASE: "Aguardando liberação da Apple",
  PENDING_CONTRACT: "Aguardando contrato",
  PENDING_DEVELOPER_RELEASE: "Aguardando liberação do desenvolvedor",
  PREORDER_READY_FOR_SALE: "Pré-venda disponível",
  PREPARE_FOR_SUBMISSION: "Preparando para envio",
  PROCESSING_FOR_APP_STORE: "Processando para a App Store",
  PROCESSING_FOR_DISTRIBUTION: "Processando para distribuição",
  READY_FOR_REVIEW: "Pronto para revisão",
  READY_FOR_SALE: "Disponível na loja",
  READY_FOR_DISTRIBUTION: "Pronto para distribuição",
  REJECTED: "Rejeitado",
  REMOVED_FROM_SALE: "Removido da venda",
  REPLACED_WITH_NEW_VERSION: "Substituído por nova versão",
  WAITING_FOR_EXPORT_COMPLIANCE: "Aguardando conformidade de exportação",
  WAITING_FOR_REVIEW: "Aguardando revisão",
  NOT_APPLICABLE: "Não aplicável",
  PENDING_RELEASE: "Aguardando liberação",
  EXPIRED: "Expirado",
};
const ascStatePt = (s: string | null | undefined): string => (s ? ASC_STATE_PT[s] || s : "");

// Estados em que a submissão travou e exige ação nossa — merecem alerta vermelho, não "aguardando".
const BLOCKED_STATES = [
  "REJECTED", "METADATA_REJECTED", "INVALID_BINARY", "DEVELOPER_REJECTED",
  "REMOVED_FROM_SALE", "DEVELOPER_REMOVED_FROM_SALE", "EXPIRED",
];
const isBlockedState = (s: string | null | undefined): boolean => !!s && BLOCKED_STATES.includes(s);

// google-play-scraper devolve o erro cru do scraping; 404 = a listagem ainda não está no ar.
// Isso não é falha de integração, é o app não publicado — vira estado "ainda não disponível".
const isNotPublishedError = (error: string | null): boolean =>
  !!error && /404|not found/i.test(error);

interface StoreVersionData {
  iosVersion: string | null; iosBuild: string | null; iosState: string | null;
  iosLiveVersion: string | null; iosLiveBuild: string | null;
  iosPending: boolean; iosAvailable: boolean;
  iosCheckedAt: string | null; iosError: string | null;
  androidVersion: string | null; androidInstalls: string | null;
  androidAvailable: boolean;
  androidCheckedAt: string | null; androidError: string | null;
  latestRelease: { version: string } | null;
  iosOutdated: boolean; androidOutdated: boolean;
}

interface StoreCardProps {
  title: string;
  /** Versão que o cliente consegue baixar HOJE. null = nada publicado na loja. */
  liveVersion: string | null;
  /** Linha de apoio da versão publicada (build, instalações). */
  liveSub?: string | null;
  outdated: boolean;
  /** Submissão em trânsito, exibida separada pra não ser confundida com o que está no ar. */
  pendingVersion?: string | null;
  pendingSub?: string | null;
  pendingBlocked?: boolean;
  notPublishedHint: string;
  checkedAt: string | null;
  error: string | null;
}

const StoreCard: React.FC<StoreCardProps> = ({
  title, liveVersion, liveSub, outdated,
  pendingVersion, pendingSub, pendingBlocked, notPublishedHint, checkedAt, error,
}) => {
  const available = !!liveVersion;
  const statusColor = !available ? "warning" : outdated ? "error" : "success";
  const statusLabel = !available
    ? "Ainda não disponível"
    : outdated ? "Desatualizada" : "Em dia";

  return (
    <Paper
      sx={{
        p: 2,
        height: "100%",
        borderLeft: 4,
        borderColor: error ? "error.main" : `${statusColor}.main`,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>{title}</Typography>
      {error ? (
        <Typography variant="body2" color="error">{error}</Typography>
      ) : (
        <>
          <Typography variant="h6" color={available ? "text.primary" : "text.secondary"}>
            {available ? liveVersion : "Não publicado"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {available ? liveSub || "Disponível na loja" : notPublishedHint}
          </Typography>
          <Chip size="small" label={statusLabel} color={statusColor} sx={{ mt: 1 }} />
        </>
      )}

      {pendingVersion && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <HourglassEmptyIcon
              fontSize="small"
              color={pendingBlocked ? "error" : "warning"}
              sx={{ mt: "2px" }}
            />
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: "bold" }}
                color={pendingBlocked ? "error.main" : "warning.main"}
              >
                {pendingBlocked ? "Submissão parada" : "Enviada, não liberada"}: {pendingVersion}
              </Typography>
              {pendingSub && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {pendingSub}
                </Typography>
              )}
            </Box>
          </Box>
        </>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        {checkedAt ? `Verificado ${dayjs(checkedAt).fromNow()}` : "Nunca verificado"}
      </Typography>
    </Paper>
  );
};

const StoreVersionPanel: React.FC = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const [data, setData] = useState<StoreVersionData | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const chainId = record?.id;

  const load = useCallback(async () => {
    if (!chainId) return;
    setLoading(true);
    try {
      const body = await get<{ data: StoreVersionData }>(`${DEFAULT_API_ADMIN}/chains/${chainId}/store-version`);
      setData(body.data);
    } catch (e) {
      notify("Erro ao carregar a versão da loja.", { type: "error" });
    } finally {
      setLoading(false);
    }
  }, [chainId, notify]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    if (!chainId) return;
    setRefreshing(true);
    try {
      const body = await post<{ data: StoreVersionData }>(
        `${DEFAULT_API_ADMIN}/chains/${chainId}/store-version/refresh`,
        {},
      );
      setData(body.data);
      notify("Versão verificada.", { type: "success" });
    } catch (e) {
      notify("Erro ao atualizar.", { type: "error" });
    } finally {
      setRefreshing(false);
    }
  };

  if (!chainId) return null;

  const iosPendingSub = data
    ? [data.iosBuild ? `build ${data.iosBuild}` : null, ascStatePt(data.iosState)]
        .filter(Boolean).join(" · ")
    : null;

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>Versão nas lojas</Typography>
        {data?.latestRelease && (
          <Typography variant="body2" color="text.secondary">
            Última versão gerada: {data.latestRelease.version}
          </Typography>
        )}
        <Button type="button" size="small" variant="outlined" onClick={refresh} disabled={refreshing || loading}>
          {refreshing ? "Verificando…" : "Verificar agora"}
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        Os números abaixo são o que o cliente consegue baixar agora. Versão enviada e ainda em análise
        aparece destacada, separada da que está no ar.
      </Typography>
      {loading && !data ? (
        <CircularProgress size={24} />
      ) : data ? (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <StoreCard
              title="App Store (iOS)"
              liveVersion={data.iosLiveVersion}
              liveSub={data.iosLiveBuild ? `build ${data.iosLiveBuild} · Disponível na loja` : "Disponível na loja"}
              outdated={data.iosOutdated}
              pendingVersion={data.iosPending ? data.iosVersion : null}
              pendingSub={data.iosPending ? iosPendingSub : null}
              pendingBlocked={isBlockedState(data.iosState)}
              notPublishedHint="Nenhuma versão liberada na App Store"
              checkedAt={data.iosCheckedAt}
              error={isNotPublishedError(data.iosError) ? null : data.iosError}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <StoreCard
              title="Play Store (Android)"
              liveVersion={data.androidVersion}
              liveSub={data.androidInstalls ? `${data.androidInstalls} instalações` : null}
              outdated={data.androidOutdated}
              notPublishedHint="Nenhuma versão liberada na Play Store"
              checkedAt={data.androidCheckedAt}
              error={isNotPublishedError(data.androidError) ? null : data.androidError}
            />
          </Grid>
        </Grid>
      ) : null}
    </Box>
  );
};

export default StoreVersionPanel;
